import { Helmet } from "react-helmet-async";
import {
  Box,
  Flex,
  Input,
  Button,
  Text,
  Image,
  VStack,
  Heading,
} from "@chakra-ui/react";
import { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import normalizeSectorName from "../utils/normalizeSectorName";
import PublicLayout from "../components/PublicLayout";

export default function FindFlyer() {
  const [postcode, setPostcode] = useState("");
  const navigate = useNavigate();

  const handleSearch = () => {
    const sector = normalizeSectorName(postcode);
    if (sector) navigate(`/sector/${sector}`);
  };

  return (
    <PublicLayout>
      <Helmet>
        <title>Find Your Postcode Coupons | postcode.coupons</title>
        <meta
          name="description"
          content="Enter your postcode to find your postcode coupons and see every local coupon in one place."
        />
        <link rel="canonical" href="https://postcode.coupons/find" />

        <meta property="og:title" content="Find Your Postcode Coupons" />
        <meta
          property="og:description"
          content="Discover postcode coupons for your area and explore local deals by postcode sector."
        />
        <meta
          property="og:image"
          content="https://postcode.coupons/og/find-flyer.png"
        />
        <meta property="og:url" content="https://postcode.coupons/find" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Find Your Postcode Coupons" />
        <meta
          name="twitter:description"
          content="Discover postcode coupons for your area and explore local deals by postcode sector."
        />
        <meta
          name="twitter:image"
          content="https://postcode.coupons/og/find-flyer.png"
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Find Your Postcode Coupons",
            url: "https://postcode.coupons/find",
            description:
              "Enter your postcode to find your postcode coupons and see every local coupon in one place.",
          })}
        </script>
      </Helmet>

      <Flex
        direction="column"
        align="center"
        justify="center"
        minH="70vh"
        px={4}
        bgGradient="linear(to-b, white, gray.50)"
      >
        <Image src="/logo512.png" boxSize="120px" mb={6} />

        <VStack spacing={3} mb={8}>
          <Heading fontSize="4xl" textAlign="center">
            Find your postcode coupons
          </Heading>
          <Text fontSize="lg" color="gray.600" textAlign="center">
            Enter your postcode to see every local coupon and all postcode coupons available in your sector.
          </Text>
        </VStack>

        <Flex
          w="100%"
          maxW="600px"
          bg="white"
          p={3}
          borderRadius="full"
          boxShadow="lg"
          align="center"
        >
          <Input
            placeholder="e.g. FY4 1AB"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            border="none"
            fontSize="lg"
          />
          <Button
            colorScheme="red"
            borderRadius="full"
            px={8}
            onClick={handleSearch}
          >
            Search
          </Button>
        </Flex>

        <Box mt={12} textAlign="center" maxW="800px">
          <Heading size="md" mb={4}>
            How it works
          </Heading>

          <Flex justify="center" gap={8} flexWrap="wrap">
            <Box maxW="220px">
              <Heading size="sm" mb={1}>
                1. Search
              </Heading>
              <Text fontSize="sm" color="gray.600">
                Enter your postcode to find your postcode coupons for your sector.
              </Text>
            </Box>

            <Box maxW="220px">
              <Heading size="sm" mb={1}>
                2. Explore
              </Heading>
              <Text fontSize="sm" color="gray.600">
                Browse local coupons, households reached, and available slots in your Coupon Flyer.
              </Text>
            </Box>

            <Box maxW="220px">
              <Heading size="sm" mb={1}>
                3. Book
              </Heading>
              <Text fontSize="sm" color="gray.600">
                Reserve a slot and put your offer into every home in your postcode sector.
              </Text>
            </Box>
          </Flex>
        </Box>

        <Box
          mt={16}
          p={6}
          bg="gray.50"
          textAlign="center"
          w="100%"
          maxW="800px"
          borderRadius="lg"
        >
          <Heading size="sm" mb={2}>
            For businesses
          </Heading>
          <Text mb={4} color="gray.700">
            Advertise in your local Coupon Flyer and put your offer in every household’s hands.
          </Text>
          <Button as={RouterLink} to="/signup?business=true" colorScheme="red">
            Start Advertising
          </Button>
        </Box>
      </Flex>
    </PublicLayout>
  );
}
